import {
  EntryTypeCover,
  EntryTypeTicketed,
  type EntryType,
} from '@/lib/api/apiTypes';

export type EventEditorValidationDraft = {
  title: string;
  description: string;
  tags: string[];
  entryType: EntryType;
  coverPrice: string;
  ticketUrl: string;
  venueName: string;
  address: string;
  latitude: number;
  longitude: number;
  startsDate: string;
  startsTime: string;
  doorsDate: string;
  doorsTime: string;
  endsDate: string;
  endsTime: string;
  contactEmail: string;
  posters: {
    status: 'uploading' | 'uploaded' | 'failed';
  }[];
  shortDescription: string;
};

export type EventEditorValidationField =
  | 'title'
  | 'description'
  | 'tags'
  | 'coverPrice'
  | 'ticketUrl'
  | 'venueName'
  | 'address'
  | 'location'
  | 'doorsAt'
  | 'startsAt'
  | 'endsAt'
  | 'contactEmail'
  | 'posters'
  | 'shortDescription';

export type EventEditorValidationIssue = {
  field: EventEditorValidationField;
  message: string;
};

export function validateEventEditorDraft(
  draft: EventEditorValidationDraft,
  hasPinnedLocation: boolean,
) {
  return (
    validateEventEditorStep(0, draft, hasPinnedLocation) ??
    validateEventEditorStep(1, draft, hasPinnedLocation) ??
    validateEventEditorStep(2, draft, hasPinnedLocation)
  );
}

export function validateEventEditorStep(
  step: number,
  draft: EventEditorValidationDraft,
  hasPinnedLocation: boolean,
) {
  return getEventEditorStepIssues(step, draft, hasPinnedLocation)[0]?.message ?? null;
}

export function getEventEditorStepIssues(
  step: number,
  draft: EventEditorValidationDraft,
  hasPinnedLocation: boolean,
): EventEditorValidationIssue[] {
  if (step === 0) {
    const issues: EventEditorValidationIssue[] = [];
    if (!draft.title.trim()) {
      issues.push({ field: 'title', message: 'Add an event title.' });
    }
    if (!draft.description.trim()) {
      issues.push({ field: 'description', message: 'Add a description.' });
    }
    if (!draft.tags.length) {
      issues.push({ field: 'tags', message: 'Choose at least one tag.' });
    }
    if (draft.tags.length > 5) {
      issues.push({ field: 'tags', message: 'Choose no more than five tags.' });
    }
    return issues;
  }

  if (step === 1) {
    const issues: EventEditorValidationIssue[] = [];
    if (
      (draft.entryType === EntryTypeCover || draft.entryType === EntryTypeTicketed) &&
      !draft.coverPrice.trim()
    ) {
      issues.push({
        field: 'coverPrice',
        message: draft.entryType === EntryTypeTicketed
          ? 'Add the starting price.'
          : 'Add the cover charge.',
      });
    } else if (
      (draft.entryType === EntryTypeCover || draft.entryType === EntryTypeTicketed) &&
      !isPositiveMoneyValue(draft.coverPrice)
    ) {
      issues.push({
        field: 'coverPrice',
        message: draft.entryType === EntryTypeTicketed
          ? 'Enter a valid starting price.'
          : 'Enter a valid cover charge.',
      });
    }
    if (draft.entryType === EntryTypeTicketed && !draft.ticketUrl.trim()) {
      issues.push({ field: 'ticketUrl', message: 'Add the ticket link.' });
    } else if (draft.entryType === EntryTypeTicketed && !isValidHTTPURL(draft.ticketUrl)) {
      issues.push({ field: 'ticketUrl', message: 'Enter a valid ticket link.' });
    }
    if (!draft.venueName.trim()) {
      issues.push({ field: 'venueName', message: 'Add the venue name.' });
    }
    if (!draft.address.trim()) {
      issues.push({ field: 'address', message: 'Add the venue address.' });
    }
    if (!hasPinnedLocation || !hasCoordinate(draft.latitude, draft.longitude)) {
      issues.push({ field: 'location', message: 'Enter an address we can place on the map.' });
    }
    const startsAt = buildDateTime(draft.startsDate, draft.startsTime);
    const doorsAt = buildDateTime(draft.doorsDate, draft.doorsTime);
    const endsAt = buildDateTime(draft.endsDate, draft.endsTime);
    if (hasPartialDateTime(draft.doorsDate, draft.doorsTime)) {
      issues.push({ field: 'doorsAt', message: 'Finish the doors date/time or remove it.' });
    }
    if (!startsAt) {
      issues.push({ field: 'startsAt', message: 'Enter a valid start date/time.' });
    }
    if (doorsAt && startsAt && doorsAt > startsAt) {
      issues.push({ field: 'doorsAt', message: 'Doors time should be before the start time.' });
    }
    if (hasPartialDateTime(draft.endsDate, draft.endsTime)) {
      issues.push({ field: 'endsAt', message: 'Finish the end date/time or remove it.' });
    }
    if (endsAt && startsAt && (!endsAt.getTime() || endsAt <= startsAt)) {
      issues.push({ field: 'endsAt', message: 'End time must be after the start time.' });
    }
    return issues;
  }

  const issues: EventEditorValidationIssue[] = [];
  if (draft.contactEmail.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.contactEmail.trim())) {
    issues.push({ field: 'contactEmail', message: 'Enter a valid contact email.' });
  }
  if (!draft.posters.some((poster) => poster.status === 'uploaded')) {
    issues.push({ field: 'posters', message: 'Add a poster to publish.' });
  }
  if (draft.posters.some((poster) => poster.status === 'uploading')) {
    issues.push({ field: 'posters', message: 'Finish uploading poster images before publishing.' });
  }
  if (draft.posters.some((poster) => poster.status === 'failed')) {
    issues.push({ field: 'posters', message: 'Retry or remove failed poster uploads before publishing.' });
  }
  if (draft.shortDescription.trim().length > 140) {
    issues.push({ field: 'shortDescription', message: 'Keep the short description under 140 characters.' });
  }
  return issues;
}

function buildDateTime(dateValue: string, timeValue: string) {
  if (!dateValue.trim() || !timeValue.trim()) {
    return null;
  }
  const value = new Date(`${dateValue}T${timeValue}:00`);
  return Number.isNaN(value.getTime()) ? null : value;
}

function hasPartialDateTime(dateValue: string, timeValue: string) {
  return Boolean(dateValue.trim()) !== Boolean(timeValue.trim());
}

function hasCoordinate(latitude: number, longitude: number) {
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return false;
  }
  return !(latitude === 0 && longitude === 0);
}

function isPositiveMoneyValue(value: string) {
  const normalized = value.trim().replace(/^\$/, '');
  if (!/^\d+(\.\d{1,2})?$/.test(normalized)) {
    return false;
  }
  const amount = Number.parseFloat(normalized);
  return Number.isFinite(amount) && amount > 0;
}

function isValidHTTPURL(value: string) {
  try {
    const parsed = new URL(value.trim());
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}
